import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Ruler, ListTodo, CornerDownRight } from "lucide-react";
import { Reveal, Chapter, SectionHeading } from "./Reveal";

export const Automation = () => {
  const [cycle, setCycle] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setCycle((c) => c + 1), 9000);
    return () => clearInterval(t);
  }, []);

  return (
    <section className="bg-diem-paper py-28 sm:py-36 overflow-hidden" data-testid="automation-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <Chapter index="05" label="Organisation second" />
        <div className="mt-8 grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <Reveal delay={0.1}>
              <SectionHeading>Organisation without the organising.</SectionHeading>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-7 text-lg leading-relaxed text-diem-slate max-w-lg">
                Deyem understands that people don't want another system to manage.
              </p>
              <p className="mt-4 text-lg leading-relaxed text-diem-ink font-medium max-w-lg">
                Keep talking naturally. Deyem does the filing around you.
              </p>
            </Reveal>
          </div>

          <Reveal delay={0.2}>
            <div key={cycle} className="rounded-3xl bg-white border border-diem-line shadow-[0_24px_60px_-16px_rgba(15,23,42,0.15)] p-6 sm:p-8" data-testid="automation-demo">
              <div className="space-y-5">
                <div>
                  <motion.div
                    initial={{ opacity: 0, y: 14 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3, duration: 0.5 }}
                    className="max-w-[90%] rounded-2xl rounded-bl-md bg-diem-mist px-4 py-3 text-[14px] text-diem-ink"
                  >
                    "Driveway clear opening is{" "}
                    <span className="relative font-bold text-diem-blue">
                      4200mm
                      <motion.span
                        className="absolute inset-x-0 -bottom-0.5 h-[2px] bg-diem-blue origin-left"
                        initial={{ scaleX: 0 }}
                        animate={{ scaleX: 1 }}
                        transition={{ delay: 1.1, duration: 0.5 }}
                      />
                    </span>
                    ."
                  </motion.div>
                  <motion.div
                    initial={{ opacity: 0, x: -10, scale: 0.9 }}
                    animate={{ opacity: 1, x: 0, scale: 1 }}
                    transition={{ delay: 1.6, type: "spring", stiffness: 300, damping: 20 }}
                    className="mt-2 ml-6 inline-flex items-center gap-1.5 text-[11px] font-bold text-diem-blue"
                  >
                    <CornerDownRight className="w-3.5 h-3.5" /> Measurement · 4200mm
                  </motion.div>
                </div>

                <div>
                  <motion.div
                    initial={{ opacity: 0, y: 14 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 2.4, duration: 0.5 }}
                    className="max-w-[90%] ml-auto rounded-2xl rounded-br-md bg-gradient-to-r from-[#1E3A8A] to-[#38BDF8] px-4 py-3 text-[14px] text-white"
                  >
                    "Please order the gate motors for Friday."
                  </motion.div>
                  <motion.div
                    initial={{ opacity: 0, x: 10, scale: 0.9 }}
                    animate={{ opacity: 1, x: 0, scale: 1 }}
                    transition={{ delay: 3.2, type: "spring", stiffness: 300, damping: 20 }}
                    className="mt-2 mr-6 flex justify-end text-[11px] font-bold text-diem-blue"
                  >
                    <span className="inline-flex items-center gap-1.5">
                      Task · Order gate motors · Due Friday <CornerDownRight className="w-3.5 h-3.5 -scale-y-100" />
                    </span>
                  </motion.div>
                </div>

                <motion.div
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 4.0, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                  className="rounded-2xl bg-diem-ink p-5"
                >
                  <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-diem-sky mb-3">
                    Oakfield Drive · Project
                  </p>
                  <AnimatePresence>
                    <div className="space-y-2">
                      <motion.div
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 4.4, duration: 0.45 }}
                        className="flex items-center gap-3 rounded-xl bg-white/5 border border-white/10 px-3.5 py-2.5"
                      >
                        <Ruler className="w-4 h-4 text-diem-sky shrink-0" />
                        <div>
                          <p className="text-[10px] font-bold uppercase tracking-wider text-white/40">Measurements</p>
                          <p className="text-[13px] font-semibold text-white">4200mm — driveway opening</p>
                        </div>
                      </motion.div>
                      <motion.div
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 4.9, duration: 0.45 }}
                        className="flex items-center gap-3 rounded-xl bg-white/5 border border-white/10 px-3.5 py-2.5"
                      >
                        <ListTodo className="w-4 h-4 text-diem-sky shrink-0" />
                        <div>
                          <p className="text-[10px] font-bold uppercase tracking-wider text-white/40">Tasks</p>
                          <p className="text-[13px] font-semibold text-white">
                            Order gate motors <span className="text-diem-warn">· Due Friday</span>
                          </p>
                        </div>
                      </motion.div>
                    </div>
                  </AnimatePresence>
                </motion.div>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
